import { useMemo, useState } from "react";
import { evento } from "../telemetria.js";
import { Link } from "react-router-dom";
import {
  competencia, competenciaAnterior, formatBRL, paraISO,
  mensalidadePara, type Apuracao, type Centavos,
} from "@ida/core";
import { entrarComGoogle } from "../firebase.js";
import { RodapeLegal } from "../componentes/Legal.js";
import { CabecalhoPublico } from "../componentes/Marca.js";
import { MetadadosPagina } from "../componentes/Metadados.js";
import { Aviso, Botao, Campo, CampoMoeda } from "../componentes/ui.js";
import "./landing.css";
import "./diagnostico.css";
import { calcularDiagnostico, despesasDiagnostico, imovelDiagnosticoValido, novoImovelDiagnostico, type ImovelDiagnostico } from "./calculo-diagnostico.js";

const ENDPOINT_INTERESSE =
  `https://southamerica-east1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}` +
  ".cloudfunctions.net/registrarInteresse";

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho",
  "agosto", "setembro", "outubro", "novembro", "dezembro"];

type Situacao = "deve_mensal" | "nada_a_pagar";

interface Resultado {
  apuracao: Apuracao;
  aluguelMensal: Centavos;
  situacao: Situacao;
}

/**
 * Diagnostico publico, sem cadastro. Roda inteiro no navegador: o motor de
 * calculo ja esta no bundle, entao a resposta e instantanea e nao custa nada
 * por consulta.
 *
 * Considera recebimentos, despesas dos imoveis e dependentes.
 *
 * O resultado bifurca em dois, e cada ramo tem um destino diferente:
 *  - deve imposto mensal -> comecar agora, e o cliente do produto pago
 *  - nao deve            -> dizemos que nao precisa. Mentir aqui destruiria
 *                           a unica coisa que nos diferencia.
 */
export function Diagnostico() {
  const [alugueis, setAlugueis] = useState<ImovelDiagnostico[]>([novoImovelDiagnostico()]);
  const [dependentes, setDependentes] = useState(0);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [registrado, setRegistrado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const aluguelMensal = alugueis.reduce((s, v) => s + v.recebido, 0);
  const podeCalcular = aluguelMensal > 0 && alugueis.every(imovelDiagnosticoValido);

  function mudarImovel(indice: number, campo: keyof ImovelDiagnostico, valor: Centavos) {
    setAlugueis((itens) => itens.map((item, i) => i === indice ? { ...item, [campo]: valor } : item));
  }

  // Ultimo mes fechado: e sobre ele que existe (ou nao) uma guia hoje.
  const comp = useMemo(() => {
    const hoje = new Date();
    return competenciaAnterior(competencia(hoje.getFullYear(), hoje.getMonth() + 1));
  }, []);

  function calcular() {
    if (!podeCalcular) return;
    const apuracao = calcularDiagnostico(alugueis, comp, dependentes);

    setResultado({
      apuracao,
      aluguelMensal,
      situacao: apuracao.imposto > 0 ? "deve_mensal" : "nada_a_pagar",
    });
    evento("diagnostico_resultado", {
      situacao: apuracao.imposto > 0 ? "deve_mensal" : "nada_a_pagar",
    });
    // O veredito nasce acima da dobra: sem isto a pessoa continua olhando o
    // formulario que acabou de sumir.
    window.scrollTo({ top: 0 });
  }

  async function registrarInteresse() {
    if (!resultado) return;
    setEnviando(true); setErro(null);
    try {
      const r = await fetch(ENDPOINT_INTERESSE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          aluguelMensal: resultado.aluguelMensal,
          impostoMensal: resultado.apuracao.imposto,
          situacao: resultado.situacao,
        }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setRegistrado(true);
      evento("diagnostico_lead");
    } catch {
      setErro("Não consegui registrar agora. Tente de novo em instantes.");
    } finally { setEnviando(false); }
  }

  return (
    <>
      <MetadadosPagina caminho="/calculadora-imposto-aluguel" />
      <CabecalhoPublico
        acao={<Link to="/" className="botao botao--texto">Início</Link>}
      />
      <main className="dg">

      {!resultado ? (
        <div className="dg__com-acao">
          <div className="pilha pilha-3 dg__abertura">
            <h1>Descubra se você precisa recolher carnê-leão</h1>
            <p className="suave">
              Informe os recebimentos e as despesas dos imóveis e veja o resultado na hora. Sem cadastro, sem senha do
              gov.br, sem enviar nada para ninguém: a conta é feita aqui no seu
              navegador.
            </p>
          </div>

          <section className="dg__secao">
            <div className="dg__secao-cab">
              <span className="dg__secao-num">01</span>
              <h2>Seus aluguéis</h2>
            </div>
            <p className="dg__secao-dica">
              Some tudo que você recebe de pessoas físicas. O que conta é quem
              mora no imóvel: com imobiliária administrando, continua entrando.
              Se o inquilino for empresa, não entra: nesse caso o imposto já é
              retido na fonte.
            </p>
            <div className="pilha pilha-3">
              {alugueis.map((imovel, i) => (
                <div className="pilha pilha-3" key={i}>
                  <div className="dg__imovel">
                    <CampoMoeda
                      rotulo={`Imóvel ${i + 1}: total pago pelo inquilino no mês`}
                      dica="Inclua aluguel, condomínio, IPTU e outros valores pagos pelo inquilino, inclusive a taxa retida pela imobiliária. Informe as despesas separadamente abaixo para descontá-las no cálculo."
                      valor={imovel.recebido}
                      aoMudar={(c) => mudarImovel(i, "recebido", c)}
                    />
                    {alugueis.length > 1 && (
                      <button className="dg__remover" type="button"
                        onClick={() => setAlugueis((a) => a.filter((_, j) => j !== i))}>
                        Remover
                      </button>
                    )}
                  </div>
                  <details className="dg__despesas">
                    <summary>Despesas do imóvel {i + 1} · {formatBRL(despesasDiagnostico(imovel))}</summary>
                    <div className="pilha pilha-3">
                      <p className="dg__secao-dica">Informe as despesas do mês que ficaram por sua conta. Não desconte novamente valores já retirados do total informado.</p>
                      <CampoMoeda rotulo="IPTU" valor={imovel.iptu} aoMudar={(c) => mudarImovel(i, "iptu", c)} />
                      <CampoMoeda rotulo="Condomínio" valor={imovel.condominio} aoMudar={(c) => mudarImovel(i, "condominio", c)} />
                      <CampoMoeda rotulo="Taxa da imobiliária" valor={imovel.administracao} aoMudar={(c) => mudarImovel(i, "administracao", c)} />
                      <CampoMoeda rotulo="Outras despesas de cobrança" dica="Despesas para cobrar ou receber o aluguel. Não inclua reformas, financiamento ou gastos pessoais."
                        valor={imovel.outras} aoMudar={(c) => mudarImovel(i, "outras", c)} />
                    </div>
                  </details>
                  {!imovelDiagnosticoValido(imovel) ? <Aviso tom="atencao">As despesas do imóvel {i + 1} não podem superar o total recebido. Revise os valores.</Aviso> : (
                    <div className="dg__soma"><span>Rendimento líquido do imóvel</span><strong>{formatBRL(imovel.recebido - despesasDiagnostico(imovel))}</strong></div>
                  )}
                </div>
              ))}
              <Botao variante="secundario" onClick={() => setAlugueis((a) => [...a, novoImovelDiagnostico()])}>
                Adicionar outro imóvel
              </Botao>
              {alugueis.length > 1 && (
                <div className="dg__soma">
                  <span className="suave">Soma dos aluguéis</span>
                  <strong>{formatBRL(aluguelMensal)}</strong>
                </div>
              )}
            </div>
          </section>

          <section className="dg__secao">
            <div className="dg__secao-cab">
              <span className="dg__secao-num">02</span>
              <h2>Seus dependentes</h2>
            </div>
            <p className="dg__secao-dica">
              Cada dependente reduz a base de cálculo do mês. Só entram os que
              você declara como dependentes na Receita.
            </p>
            <div className="pilha pilha-3">
              <Campo rotulo="Quantos dependentes" type="number" min={0} inputMode="numeric"
                value={dependentes}
                onChange={(e) => setDependentes(Math.max(0, Number(e.target.value) || 0))} />
            </div>
          </section>

          <div className="dg__acao">
            <Botao bloco onClick={calcular} disabled={!podeCalcular}>
              Ver meu resultado
            </Botao>
          </div>
        </div>
      ) : (
        <Resultado
          resultado={resultado}
          comp={comp}
          email={email}
          setEmail={setEmail}
          enviando={enviando}
          registrado={registrado}
          erro={erro}
          onRegistrar={() => void registrarInteresse()}
          onRefazer={() => {
            setResultado(null); setRegistrado(false); window.scrollTo({ top: 0 });
          }}
        />
      )}
      </main>
    </>
  );
}

function Resultado({
  resultado, comp, email, setEmail, enviando, registrado, erro, onRegistrar, onRefazer,
}: {
  resultado: Resultado;
  comp: { ano: number; mes: number };
  email: string;
  setEmail: (v: string) => void;
  enviando: boolean;
  registrado: boolean;
  erro: string | null;
  onRegistrar: () => void;
  onRefazer: () => void;
}) {
  const { apuracao, situacao } = resultado;
  const mes = MESES[comp.mes - 1];

  return (
    <div>
      <div className={`dg__veredito ${situacao === "deve_mensal" ? "dg__veredito--deve" : ""}`}>
        {situacao === "deve_mensal" && (
          <>
            <span className="dg__veredito-rotulo">Você precisa recolher</span>
            <div className="dg__veredito-valor">{formatBRL(apuracao.imposto)}</div>
            <p className="dg__veredito-frase">
              referente a {mes} de {comp.ano}, com vencimento em{" "}
              <strong>{paraISO(apuracao.vencimento).split("-").reverse().join("/")}</strong>.
              Depois disso corre multa de 0,33% ao dia.
            </p>
          </>
        )}
        {situacao === "nada_a_pagar" && (
          <>
            <span className="dg__veredito-rotulo">Sem imposto neste cálculo</span>
            <div className="dg__veredito-valor">R$ 0,00</div>
            <p className="dg__veredito-frase">
              Pelos dados informados, o cálculo deste mês ficou em R$ 0,00.
              O Alugando continua cuidando da organização dos aluguéis,
              dos recebimentos e dos comprovantes. A cada mês, você confirma
              se recebeu outros rendimentos sujeitos ao Carnê-Leão, além dos
              aluguéis, antes de seguir com a apuração.
            </p>
          </>
        )}
      </div>

      <section className="dg__resultado-secao">
        <span className="rotulo">Como chegamos nesse número</span>
        <div className="dg__conta">
        <div className="dg__conta-linha">
          <span>Aluguéis recebidos no mês</span><span>{formatBRL(apuracao.totalRecebido)}</span>
        </div>
        <div className="dg__conta-linha">
          <span>Despesas dos imóveis</span><span>− {formatBRL(apuracao.despesasImovel)}</span>
        </div>
        <div className="dg__conta-linha">
          <span>Rendimento líquido dos aluguéis</span><span>{formatBRL(apuracao.rendimentoLiquido)}</span>
        </div>
        <div className="dg__conta-linha">
          <span>{apuracao.usouDescontoSimplificado ? "Desconto simplificado" : "Dedução por dependentes"}</span><span>− {formatBRL(apuracao.deducaoAplicada)}</span>
        </div>
        <div className="dg__conta-linha">
          <span>Base de cálculo</span><span>{formatBRL(apuracao.baseCalculo)}</span>
        </div>
        <div className="dg__conta-linha">
          <span>Imposto pela tabela</span><span>{formatBRL(apuracao.impostoTabela)}</span>
        </div>
        {apuracao.redutor > 0 && (
          <div className="dg__conta-linha">
            <span>Redutor da Lei 15.270/2025</span><span>− {formatBRL(apuracao.redutor)}</span>
          </div>
        )}
        <div className="dg__conta-linha dg__conta-linha--total">
          <span>DARF do mês</span><span>{formatBRL(apuracao.imposto)}</span>
        </div>
        </div>
      </section>

      {situacao === "deve_mensal" ? (
        <section className="dg__resultado-secao pilha pilha-3">
          <span className="rotulo">O que fazer agora</span>
          <h2 className="lp__titulo">Não precisa fazer isso na mão todo mês.</h2>
          <p className="suave">
            A gente pergunta quanto entrou, calcula, emite a guia e te lembra
            até o pagamento. Tudo pelo WhatsApp, sem entrar no e-CAC.
          </p>
          <Botao bloco onClick={() => void entrarComGoogle()}>
            Começar agora pelo WhatsApp
          </Botao>
          <span className="tenue">
            Primeiro mês grátis; depois {formatBRL(mensalidadePara(resultado.aluguelMensal))} por
            mês para a sua faixa de aluguéis
          </span>
        </section>
      ) : (
        <section className="dg__resultado-secao pilha pilha-3">
          <span className="rotulo">Organize seus aluguéis</span>
          <p className="suave">
            Acompanhe recebimentos, reúna comprovantes e mantenha seus aluguéis
            organizados, mesmo nos meses sem imposto a recolher.
          </p>
          <Botao bloco onClick={() => void entrarComGoogle()}>Começar agora</Botao>
          {registrado ? (
            <Aviso>Cadastro de interesse recebido.</Aviso>
          ) : (
            <>
              <Campo rotulo="Seu email (opcional)" type="email" inputMode="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@email.com" />
              {erro && <Aviso tom="atencao">{erro}</Aviso>}
              <Botao bloco variante="secundario" onClick={onRegistrar}
                disabled={enviando || !email.includes("@")}>
                {enviando ? "Registrando…" : "Quero receber novidades"}
              </Botao>
            </>
          )}
        </section>
      )}

      <div className="pilha pilha-3 dg__rodape">
        <Botao variante="texto" onClick={onRefazer}>Refazer com outros valores</Botao>
        <p className="dg__nota">
          Cálculo pelas regras vigentes em {comp.ano}, conferido contra o
          demonstrativo oficial do Carnê-Leão Web. É uma estimativa a partir do
          que você informou: a responsabilidade pelo tributo é do contribuinte,
          e o valor oficial da guia é o emitido pelo Sicalc.
        </p>
        <RodapeLegal />
      </div>
    </div>
  );
}
